import { AppSelectors } from 'src/app/store/app/app.selectors';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy
} from '@angular/core';
import {
  Meal,
  MealTime,
  PopularItem,
  SalesByDinerTypeResponse,
  SalesByPaymentTypeResponse,
  SalesCountAndAmount,
  TotalsResponse,
  SharedPosLocation
} from '../../../models';
import { MealHelper } from '../../../helpers';
import { Store } from '@ngxs/store';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { interval, Subscription, Subject } from 'rxjs';
import { distinctUntilChanged, filter, skip, takeUntil } from 'rxjs/operators';
import { ReactiveComponent } from '../../../components/shared/reactive-component';
import {
  GetRealtimeInfo,
  PosManagerSelectors
} from '../../../store/pos-manager';
import { APP_STATE_TOKEN } from '../../../store/app/app.state.model';
import { faChevronRight } from '@fortawesome/pro-light-svg-icons';
import { Router } from '@angular/router';
import { PageConstant } from '../../../constants';
import { LanguageService } from 'src/app/services/language.service';

@Component({
  templateUrl: './realtime-info-menu.page.html',
  styleUrls: ['./realtime-info-menu.page.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RealtimeInfoMenuPage
  extends ReactiveComponent
  implements OnDestroy
{
  @ViewSelectSnapshot(AppSelectors.getSelectedLocaleCode)
  currentLang: string;

  @ViewSelectSnapshot(PosManagerSelectors.getPosLocations)
  readonly locations: SharedPosLocation[];

  @ViewSelectSnapshot(PosManagerSelectors.getRealtimeTotals)
  readonly realtimeTotals: TotalsResponse;

  @ViewSelectSnapshot(PosManagerSelectors.getRealtimeSalesByPaymentType)
  readonly realtimeSalesByPaymentType: SalesByPaymentTypeResponse;

  @ViewSelectSnapshot(PosManagerSelectors.getRealtimeSalesByDinerType)
  readonly realtimeSalesByDinerType: SalesByDinerTypeResponse;

  @ViewSelectSnapshot(PosManagerSelectors.getRealtimeMostPopularItems)
  readonly mostPopularItems: PopularItem[];

  form: FormGroup;
  selectedDateStr: string;
  readonly MealTime = MealTime;
  readonly MEALS: Meal[] = MealHelper.MEALS;
  readonly faChevronRight = faChevronRight;
  showMostPopularItems = false;

  private autoRefreshSubscription: Subscription | null = null;
  private currentRequestSubscription: Subscription | null = null;
  private cancelCurrentRequest$ = new Subject<void>();
  private readonly AUTO_REFRESH_INTERVAL = 30000;

  private readonly paymentTypeMap = new Map<string, string>([
    ['cash', 'payment.cash'],
    ['credit_card', 'payment.credit'],
    ['bill_to_room', 'payment.billToRoom'],
    ['meal_plan', 'pos.mealPlan'],
    ['gift', 'payment.giftCard'],
    ['charge_account', 'payment.chargeAccount']
  ]);

  /**
   * Payment-type map with the Meal Plan / Charge Account rows dropped when
   * their Core sub-license is disabled (TOUCH-546). Recomputed per change
   * detection; the map is tiny (6 entries) so no memoization is warranted.
   */
  get filteredPaymentTypeMap(): Map<string, string> {
    return filterPaymentTypeMapByLicense(
      this.paymentTypeMap,
      this.realtimeSalesByPaymentType
    );
  }

  readonly dinerTypeMap = new Map<string, string>([
    ['guest', 'diners.guest'],
    ['resident', 'diners.resident'],
    ['staff', 'diners.staff']
  ]);

  readonly currentLocale$ = this.languageService.getCurrentLocale$;

  constructor(
    private readonly store: Store,
    private readonly cdr: ChangeDetectorRef,
    private readonly fb: FormBuilder,
    private readonly router: Router,
    private readonly languageService: LanguageService
  ) {
    super();
    this.selectedDateStr = new Date().toISOString();
    this.form = this.fb.group({
      selectedMealId: [MealTime.Breakfast],
      selectedLocationId: this.fb.control(
        this.store.selectSnapshot(APP_STATE_TOKEN).device?.pos_location_id
      )
    });
  }

  *onSubscribe() {
    this.callToServer();
    this.startAutoRefresh();

    yield this.form.get('selectedMealId')?.valueChanges.subscribe(() => {
      this.callToServer();
      this.startAutoRefresh();
      this.cdr.markForCheck();
    });

    yield this.form
      .get('selectedLocationId')
      ?.valueChanges.subscribe((locationId) => {
        if (this.form.get('selectedLocationId')?.value !== locationId) {
          this.form.patchValue({ selectedLocationId: locationId });
        }
        this.callToServer();
        this.startAutoRefresh();
        this.cdr.markForCheck();
      });

    yield this.store
      .select(AppSelectors.getSelectedLocaleCode)
      .pipe(
        filter((code) => !!code),
        distinctUntilChanged(),
        skip(1)
      )
      .subscribe(() => {
        this.callToServer();
        this.startAutoRefresh();
        this.cdr.markForCheck();
      });
  }

  onDateChanged(dateStr: string) {
    this.selectedDateStr = dateStr;
    this.callToServer();
    this.startAutoRefresh();
    this.cdr.markForCheck();
  }

  onTotalSaleButtonClicked() {
    this.router.navigateByUrl(
      PageConstant.POS_MANAGER_REAL_TIME_INFO_TOTAL_SALE_BY_CATEGORY_PAGE,
      {
        state: {
          selectedMealId: this.form.get('selectedMealId')?.value,
          selectedDateStr: this.selectedDateStr,
          selectedLocationId: this.form.get('selectedLocationId')?.value
        }
      }
    );
  }

  onPosOperatorsButtonClicked() {
    this.router.navigateByUrl(
      PageConstant.POS_MANAGER_REAL_TIME_INFO_POS_OPERATORS_PAGE,
      {
        state: {
          selectedMealId: this.form.get('selectedMealId')?.value,
          selectedDateStr: this.selectedDateStr,
          selectedLocationId: this.form.get('selectedLocationId')?.value
        }
      }
    );
  }

  private callToServer(): void {
    this.cancelCurrentRequest();

    const locationId: number = this.form.get('selectedLocationId')?.value;
    const selectedMealId: MealTime = this.form.get('selectedMealId')?.value;

    this.currentRequestSubscription = this.store
      .dispatch([
        new GetRealtimeInfo({
          date: this.selectedDateStr,
          pos_location: locationId,
          meal_id: selectedMealId
        })
      ])
      .pipe(takeUntil(this.cancelCurrentRequest$))
      .subscribe();
  }

  private cancelCurrentRequest(): void {
    if (this.currentRequestSubscription) {
      this.cancelCurrentRequest$.next();
      this.currentRequestSubscription.unsubscribe();
      this.currentRequestSubscription = null;
    }
  }

  /**
   * Automatically refreshes the data every 30 seconds.
   */
  private startAutoRefresh(): void {
    this.stopAutoRefresh();
    this.autoRefreshSubscription = interval(
      this.AUTO_REFRESH_INTERVAL
    ).subscribe(() => {
      this.callToServer();
      this.cdr.markForCheck();
    });
  }

  private stopAutoRefresh(): void {
    if (this.autoRefreshSubscription) {
      this.autoRefreshSubscription.unsubscribe();
      this.autoRefreshSubscription = null;
    }
  }

  showMostPopularItemsView() {
    this.showMostPopularItems = true;
    this.cdr.markForCheck();
  }

  hideMostPopularItemsView() {
    this.showMostPopularItems = false;
    this.cdr.markForCheck();
  }

  onMostPopularItemsMealChange(mealId: MealTime) {
    this.form.patchValue({ selectedMealId: mealId });
  }

  ngOnDestroy(): void {
    this.stopAutoRefresh();
    this.cancelCurrentRequest();
    this.cancelCurrentRequest$.complete();
    super.ngOnDestroy();
  }
}

/**
 * Drop the `meal_plan` / `charge_account` rows from the payment-type display
 * map when the backend reports their sub-license disabled AND that tender
 * type has no sales to show. Fails open: rows are removed only on an
 * explicit `false` with zero data, so an absent flag (older response), an
 * undefined sales payload, or a disabled license with real sales (e.g.
 * transactions made before the license was turned off) keeps the row
 * visible — otherwise those dollars vanish from this section while still
 * being counted in "By diner type", breaking the totals reconciliation.
 */
export function filterPaymentTypeMapByLicense(
  paymentTypeMap: Map<string, string>,
  sales?: SalesByPaymentTypeResponse
): Map<string, string> {
  const hasValue = (data?: SalesCountAndAmount) =>
    !!(data?.total_sales_amount || data?.total_sales_count);

  return new Map(
    Array.from(paymentTypeMap).filter(([key]) => {
      if (
        key === 'meal_plan' &&
        sales?.meal_plan_enabled === false &&
        !hasValue(sales.meal_plan)
      ) {
        return false;
      }
      if (
        key === 'charge_account' &&
        sales?.charge_account_enabled === false &&
        !hasValue(sales.charge_account)
      ) {
        return false;
      }
      return true;
    })
  );
}
